<template>
  <Modal
    :headline="$t('myVocabulary.addVocabulary')"
    :save-btn-disabled="!isValidVocabulary"
    :show-modal="showModal"
    :show-full-screen="true"
    @save="createVocabulary"
    @cancel="showModal = false">
    <v-row align="end">
      <v-col cols="10">
        <v-select
          :label="$t('myVocabulary.topic')"
          hide-details
          :no-data-text="$t('myVocabulary.noTopicCreated')"
          :items="topics"
          v-model="topic" />
      </v-col>
      <v-col cols="10">
        <MvTopicCreator
          :vocabulary-list-by-language="vocabularyListByLanguage"
          @create:topic="createTopic" />
      </v-col>
    </v-row>
    <p class="mb-1 pt-12">{{ $t('myVocabulary.addVocabularies') }}</p>
    <v-row
      v-for="(item, index) in vocabularies" :key="`mvActionButton-${index}`"
      :class="index > 0 ? 'pt-6' : 'pt-0'">
      <v-col cols="10" class="py-0">
        <v-text-field
          :tabindex="index + 1"
          :autofocus="index === vocabularies.length - 1"
          :label="`${$t('app.learningLanguage')} (${$lang.getNativeName(userLanguages.learning)})`"
          hide-details
          :lang="userLanguages.learning"
          autocomplete="off"
          v-model="item.learning" />
        <div class="pt-2">
          <GenderChooser
            :gender="gender"
            @select:gender="gender = $event" />
        </div>
      </v-col>
      <v-col cols="2" class="py-0">
        <v-btn icon x-large @click="deleteVocabulary(index)">
          <v-icon
            style="position: relative; top: 33px;"
            color="red">
            delete
          </v-icon>
        </v-btn>
      </v-col>
      <v-col cols="10">
        <v-text-field
          :tabindex="index + 2"
          :label="`${$t('app.baseLanguage')} (${$lang.getNativeName(userLanguages.base)})`"
          hide-details
          :lang="userLanguages.base"
          autocomplete="off"
          v-model="item.base" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="10">
        <v-btn
          block
          color="green"
          outlined
          @click="addVocabulary">
          <v-icon left small>add</v-icon>
          {{ $t('app.add') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-btn
      color="pink"
      fab
      dark
      fixed
      bottom
      right
      slot="activator"
      @click="onOpen"
      :class="{'is-visible': footerVisibility}">
      <v-icon>add</v-icon>
    </v-btn>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '@components/modal';
import MvTopicCreator from '@components/mv_manager/mv_topic_creator';
import GenderChooser from '@components/mv_manager/gender_chooser';
import { VOCABULARIES } from '@/config/firestore';
import mergeData from '@/mixins/firestore/mergeData';
import getDataFrom from '@/mixins/firestore/getDataFrom';
import loaderMethods from '@/mixins/loader';

export default {
  name: 'MvActionButton',
  components: {
    Modal,
    MvTopicCreator,
    GenderChooser,
  },
  mixins: [mergeData, getDataFrom, loaderMethods],
  props: {
    vocabularyListByLanguage: {
      type: Array,
      required: true,
    },
    vocabularyListKeys: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    showModal: false,
    showTopicModal: false,
    showColorModal: false,
    baseLanguage: '',
    learningLanguage: '',
    topics: [],
    topic: '',
    baseTopic: '',
    vocabularies: [{
      base: '',
      learning: '',
    }],
    gender: '',
    color: 'blue',
  }),
  computed: {
    ...mapGetters('user', ['userLanguages']),
    ...mapGetters('current', ['footerVisibility']),

    baseLanguageCode() {
      return this.$lang.getCode(this.baseLanguage);
    },
    learningLanguageCode() {
      return this.$lang.getCode(this.learningLanguage);
    },
    isValidVocabulary() {
      const isValidVocabularies = this.vocabularies.every((item) => item.base.length > 0 && item.learning.length > 0);
      const isValidAdditionalData = [this.baseLanguage, this.learningLanguage, this.topic].every((item) => item.length > 0);

      return isValidVocabularies && isValidAdditionalData;
    },
  },
  methods: {
    async onOpen() {
      this.showLoader();

      this.initLanguages();
      this.getTopics();
      this.gender = '';

      this.showModal = true;

      this.hideLoader();
    },
    initLanguages() {
      this.baseLanguage = this.$lang.getNativeName(this.userLanguages.base);
      this.learningLanguage = this.$lang.getNativeName(this.userLanguages.learning);
    },
    getTopics() {
      this.topics = this.vocabularyListByLanguage.map((item) => item[this.baseLanguageCode]);
      this.topic = this.topics.length > 0 ? this.topics[0] : '';
    },
    createTopic(baseTopic, color) {
      this.baseTopic = baseTopic;
      this.topic = baseTopic;
      this.color = color;

      this.topics.push(baseTopic);
    },
    createVocabularyForExistingTopic() {
      const topicIndex = this._.findIndex(this.vocabularyListByLanguage, {
        [this.baseLanguageCode]: this.topic,
      });
      const currentVocabularies = this._.cloneDeep(this.vocabularyListByLanguage[topicIndex].vocabularies);
      const vocabularies = this.vocabularies.map((item) => ({
        [this.baseLanguageCode]: item.base,
        [this.learningLanguageCode]: item.learning,
        [`${[this.learningLanguageCode]}_gender`]: this.gender,
      }));

      return {
        [topicIndex]: {
          [this.baseLanguageCode]: this.vocabularyListByLanguage[topicIndex][this.baseLanguageCode],
          color: this.vocabularyListByLanguage[topicIndex].color,
          lang: `${this.baseLanguageCode}-${this.learningLanguageCode}`,
          vocabularies: [
            ...currentVocabularies,
            ...vocabularies,
          ],
        },
      };
    },
    createVocabularyForNewTopic() {
      const lastVocabularyKey = this.vocabularyListKeys.length > 0
        ? this.vocabularyListKeys[this.vocabularyListKeys.length - 1]
        : 0;
      const vocabularyListIndex = lastVocabularyKey === 0
        ? 0
        : parseInt(lastVocabularyKey, 10) + 1;
      const vocabularies = this.vocabularies.map((item) => ({
        [this.baseLanguageCode]: item.base,
        [this.learningLanguageCode]: item.learning,
        [`${[this.learningLanguageCode]}_gender`]: this.gender,
      }));

      return {
        [vocabularyListIndex]: {
          [this.baseLanguageCode]: this.baseTopic,
          color: this.color,
          lang: `${this.baseLanguageCode}-${this.learningLanguageCode}`,
          vocabularies: [
            ...vocabularies,
          ],
        },
      };
    },
    async createVocabulary() {
      const vocabularyExistsInRemoteList = this._.find(this.vocabularyListByLanguage, {
        [this.baseLanguageCode]: this.base,
      });
      const vocabularyExistsInLocalList = this._.find(this.vocabularies, {
        [this.baseLanguageCode]: this.base,
      });

      if (vocabularyExistsInRemoteList || vocabularyExistsInLocalList) {
        this.setSnackbarError({ text: 'Vocabulary already exists' });
        return;
      }

      this.showModal = false;

      const topicExistsInRemoteList = this._.find(this.vocabularyListByLanguage, {
        [this.baseLanguageCode]: this.topic,
      });
      const topicExistsInLocalList = this._.find(this.vocabularies, {
        [this.baseLanguageCode]: this.topic,
      });

      const vocabularyData = (topicExistsInRemoteList || topicExistsInLocalList)
        ? this.createVocabularyForExistingTopic()
        : this.createVocabularyForNewTopic();

      const languageSet = `${this.baseLanguageCode}_${this.learningLanguageCode}`;
      const languageSets = await this.getDataFrom(`${VOCABULARIES}_langs`) || {};

      if (languageSets?.langs && !languageSets.langs.includes(languageSet)) {
        languageSets.langs.push(languageSet);
      } else {
        languageSets.langs = [languageSet];
      }

      await this.mergeData(VOCABULARIES, vocabularyData);
      await this.mergeData(`${VOCABULARIES}_langs`, languageSets);

      this.$emit('updated:vocabulary');

      this.resetData();
    },
    addVocabulary() {
      this.vocabularies.push({
        base: '',
        learning: '',
      });
    },
    deleteVocabulary(index) {
      this.$delete(this.vocabularies, index);
    },
    resetData() {
      this.vocabularies = [{
        base: '',
        learning: '',
      }];
      this.baseTopic = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.is-visible {
  bottom: 60px;
}
</style>
